import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { apiGet, formatPct, formatLKR } from "@/lib/api";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Layers, ArrowUpRight, Brain, AlertTriangle } from "lucide-react";
import SectorVerdictPill, { sectorVerdictTheme } from "@/components/common/SectorVerdictPill";

const SectorsPage = () => {
  const [data, setData] = useState([]);
  const [scores, setScores] = useState({}); // sector -> L6 score row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    Promise.all([
      apiGet("/sectors/overview"),
      apiGet("/sectors/scores").catch(() => ({ items: [] })),
    ])
      .then(([overview, scoreResp]) => {
        setData(overview);
        const map = {};
        (scoreResp?.items || []).forEach((row) => {
          if (row.sector) map[row.sector] = row;
        });
        setScores(map);
      })
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter((d) => (d.sector || "").toLowerCase().includes(q));
  }, [data, search]);

  // Phase 6: separate "Unclassified" / empty sectors from the main grid so
  // the canonical CSE GICS sectors lead the display.
  const { primary, uncategorised } = useMemo(() => {
    const isUnclassified = (s) =>
      !s.sector || s.sector === "Unclassified" || s.sector === "Other";
    return {
      primary: filtered.filter((s) => !isUnclassified(s)),
      uncategorised: filtered.filter(isUnclassified),
    };
  }, [filtered]);

  if (loading) return <LoadingState label="Loading sector overview…" />;
  if (error) return <ErrorState description={error} />;

  const totalCompanies = data.reduce((s, d) => s + (d.constituents_count || 0), 0);
  const totalBuy = data.reduce((s, d) => s + (d.signal_distribution?.BUY || 0), 0);

  return (
    <div className="space-y-6" data-testid="sectors-page">
      <Card className="border bg-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="font-display flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                CSE Sector Overview
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {data.length} sectors · {totalCompanies} listed companies · {totalBuy} BUY signals across the universe
              </p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sector"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="sectors-search-input"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {primary.map((s) => (
          <SectorCard key={s.sector} s={s} l6={scores[s.sector]} />
        ))}
      </div>

      {uncategorised.length > 0 && (
        <div className="space-y-2 pt-4 border-t border-border/60">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
            Other / Unclassified
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {uncategorised.map((s) => (
              <SectorCard key={s.sector || "_unclassified"} s={s} l6={scores[s.sector]} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Test 6 Fix 4 — bold, colour-distinct verdict tile styles. Backgrounds
// now use a stronger tint so OVER/NEUT/UNDER are immediately distinguishable
// at a glance across Sectors page and Market Intelligence sector grids.
const VERDICT_STYLES = {
  OVERWEIGHT: {
    border: "border-[hsl(142_72%_36%)]/45",
    text: "text-[hsl(142_72%_29%)]",
    bg: "bg-[hsl(142_72%_36%)]/12",
  },
  NEUTRAL: {
    border: "border-border",
    text: "text-muted-foreground",
    bg: "bg-muted/40",
  },
  UNDERWEIGHT: {
    border: "border-[hsl(0_72%_50%)]/45",
    text: "text-[hsl(0_72%_42%)]",
    bg: "bg-[hsl(0_72%_50%)]/10",
  },
};

const SectorCard = ({ s, l6 }) => {
  const dist = s.signal_distribution || {};
  const total = Object.values(dist).reduce((a, b) => a + (b || 0), 0) || 1;
  const buyPct = ((dist.BUY || 0) / total) * 100;
  const watchPct = ((dist.WATCH || 0) / total) * 100;
  const holdPct = ((dist.HOLD || 0) / total) * 100;
  return (
    <Link
      to={`/stocks?sector=${encodeURIComponent(s.sector)}`}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
      data-testid={`sector-card-link-${s.sector?.replace(/\s+/g, "-")}`}
    >
    <Card
      className="border bg-card hover:shadow-md hover:border-primary/40 transition-[box-shadow,border-color] duration-150 cursor-pointer h-full"
      data-testid={`sector-card-${s.sector?.replace(/\s+/g, "-")}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="font-display text-base truncate">{s.sector}</CardTitle>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {s.constituents_count} {s.constituents_count === 1 ? "company" : "companies"}
            </p>
          </div>
          <Badge variant="outline" className="text-[10px] shrink-0">
            {s.constituents_count} cos
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <Stat label="Median PE" value={s.median_pe?.toFixed(1)} />
          <Stat label="Median Yield" value={s.median_yield != null ? `${s.median_yield.toFixed(1)}%` : null} />
          <Stat label="Median ROE" value={s.median_roe != null ? `${s.median_roe.toFixed(1)}%` : null} />
        </div>

        {/* Layer 6 — Sector Context (Claude weekly).
            Phase 11 polish — context box bg + left border match the
            verdict colour exactly. */}
        {l6 ? (
          (() => {
            const v = (l6.overall_verdict || "NEUTRAL").toUpperCase();
            const theme = sectorVerdictTheme(v);
            return (
              <div
                className={`rounded-md ${theme.boxBg} ${theme.boxBorder} p-2.5 space-y-1`}
                data-testid={`sector-l6-${s.sector?.replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${theme.boxText}`}>
                    <Brain className="h-3 w-3" /> Sector Context · L6
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[11px] tabular-nums text-foreground/90">
                      {l6.total_score}/{l6.max_score || 10}
                    </span>
                    <SectorVerdictPill verdict={v} size="sm" />
                    {l6.is_stale && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 border-warning/40 text-warning gap-0.5">
                        <AlertTriangle className="h-2.5 w-2.5" /> stale
                      </Badge>
                    )}
                  </div>
                </div>
                {l6.sector_summary && (
                  <p className="text-[11px] leading-snug text-foreground/85 line-clamp-2">
                    {l6.sector_summary}
                  </p>
                )}
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground tabular flex-wrap">
                  <span>Cycle: {l6.cycle_position?.replace(/_/g, " ") || "—"}</span>
                  <span>·</span>
                  <span>Macro: {l6.macro_assessment || "—"}</span>
                  <span>·</span>
                  <span>Foreign: {l6.foreign_sentiment || "—"}</span>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="rounded-md border border-dashed border-border/70 bg-muted/20 p-2 text-[10.5px] text-muted-foreground">
            <span className="uppercase tracking-wider">Sector Context · L6</span>
            <span className="ml-1 italic">awaiting weekly Claude run</span>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Signal mix</div>
          <div className="h-2 rounded-full overflow-hidden bg-muted flex" data-testid={`sector-signal-bar-${s.sector?.replace(/\s+/g, "-")}`}>
            <div className="h-full bg-[hsl(var(--success))]" style={{ width: `${buyPct}%` }} title={`BUY ${dist.BUY || 0}`} />
            <div className="h-full bg-[hsl(var(--primary))]/60" style={{ width: `${watchPct}%` }} title={`WATCH ${dist.WATCH || 0}`} />
            <div className="h-full bg-muted-foreground/40" style={{ width: `${holdPct}%` }} title={`HOLD ${dist.HOLD || 0}`} />
          </div>
          <div className="mt-1.5 flex items-center gap-2 text-[10px] text-muted-foreground tabular">
            <span className="text-[hsl(var(--success))]">BUY {dist.BUY || 0}</span>
            <span>·</span>
            <span>WATCH {dist.WATCH || 0}</span>
            <span>·</span>
            <span>HOLD {dist.HOLD || 0}</span>
            {dist.NO_DATA > 0 && <><span>·</span><span>—{dist.NO_DATA}</span></>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          {s.top_score && (
            <div
              role="link"
              className="rounded-md border bg-background p-2 hover:bg-muted/30 transition-colors duration-150 cursor-pointer"
              data-testid={`sector-top-score-${s.sector?.replace(/\s+/g, "-")}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `/stocks/${s.top_score.company_code}`;
              }}
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Top score</div>
              <div className="mt-0.5 font-medium truncate flex items-center gap-1">
                {s.top_score.company_name}
                <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="text-[10px] text-muted-foreground tabular">
                {s.top_score.cse_symbol} · score {s.top_score.composite_score}
              </div>
            </div>
          )}
          {s.top_yield && (
            <div
              role="link"
              className="rounded-md border bg-background p-2 hover:bg-muted/30 transition-colors duration-150 cursor-pointer"
              data-testid={`sector-top-yield-${s.sector?.replace(/\s+/g, "-")}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `/stocks/${s.top_yield.company_code}`;
              }}
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Top yield</div>
              <div className="mt-0.5 font-medium truncate flex items-center gap-1">
                {s.top_yield.company_name}
                <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="text-[10px] text-[hsl(var(--success))] tabular">
                {formatPct(s.top_yield.dividend_yield)}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </Link>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-md border bg-background py-1.5">
    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold tabular">{value ?? "—"}</div>
  </div>
);

export default SectorsPage;
